"use client";
import {
  type FC,
  isValidElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import type { RecipeVariants } from "@vanilla-extract/recipes";
import { isString, mergeClasses } from "@huub-web/utils";
import { motion } from "motion/react";

import { Button } from "../Button/Button";
import { Icon } from "../Icon/Icon";
import { Text } from "../Text/Text";
import { IconButton } from "../IconButton/IconButton";
import {
  closeStyle,
  iconStyle,
  overlayStyle,
  snackbarContentStyle,
  snackbarStyle,
  titleStyle,
} from "./Snackbar.css";
import { ProgressBar } from "../ProgressBar/ProgressBar";
import { Edges } from "../Edges/Edges";
import type { IconName } from "../../theme";
import type { ClassNameProp } from "../../props";

import { elevations } from "../../theme/css/elevations.css";

export type SnackbarProps = RecipeVariants<typeof snackbarStyle> &
  ClassNameProp & {
    title?: ReactNode;
    body?: ReactNode;
    autoHide?: number | false;
    buttonValue?: string;
    closeable?: boolean;
    paused?: boolean;
    index?: number;
    onClose?: () => void;
  };

/**
 * Snackbar component is a floating message component.
 */
export const Snackbar: FC<SnackbarProps> = ({
  title,
  autoHide,
  buttonValue,
  variant = "default",
  closeable = false,
  paused = false,
  index = 0,
  body,
  className,
  onClose,
}) => {
  const [autoHideProgress, setAutoHideProgress] = useState(0);

  const handleCloseClick = () => {
    close();
  };

  const close = () => {
    onClose?.();
    if (animationFrameID.current) {
      cancelAnimationFrame(animationFrameID.current);
    }
  };

  const startTimeRef = useRef<number | null>(null);
  const animationFrameID = useRef<number | null>(null);

  useEffect(() => {
    if (paused || autoHide === false || autoHide === undefined) {
      return;
    }

    startTimeRef.current = performance.now();
    const duration = autoHide * 1000;

    const updateProgress = (currentTime: number) => {
      if (startTimeRef.current === null) {
        return;
      }

      const elapsed = currentTime - startTimeRef.current;
      const progress = Math.min((elapsed / duration) * 100, 100);

      setAutoHideProgress(progress);

      if (progress < 100) {
        animationFrameID.current = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameID.current = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameID.current) {
        cancelAnimationFrame(animationFrameID.current);
      }
    };
  }, [autoHide, paused]);

  useEffect(() => {
    if (autoHideProgress === 100 && !paused) {
      close();
    }
  }, [autoHideProgress, paused]);

  const icon: IconName | undefined = (() => {
    switch (variant) {
      case "success":
        return "checkCircleFilled";
      case "error":
        return "alertFilled";
      case "warning":
        return "alertFilled";
      case "default":
        return "infoFilled";
      default:
        return undefined;
    }
  })();

  return (
    <motion.div
      className={mergeClasses(
        snackbarStyle({ variant, paused }),
        elevations.l,
        className,
      )}
    >
      {autoHide !== undefined && !paused && (
        <ProgressBar
          progress={autoHideProgress}
          position="absolute"
          variant="custom"
          color="currentColor"
        />
      )}

      {closeable && (
        <IconButton
          className={closeStyle}
          icon="close"
          size="xxs"
          variant="opaqueCurrentColor"
          onClick={handleCloseClick}
          disabled={paused}
          rounded
        />
      )}

      <Edges
        leading={icon && <Icon icon={icon} size="l" className={iconStyle} />}
        className={snackbarContentStyle()}
      >
        {title && isString(title) && (
          <Text
            as="h3"
            variant="bodyL"
            markdown
            className={titleStyle({ closeable })}
          >
            {title}
          </Text>
        )}
        {title && !isString(title) && isValidElement(title) && <>{title}</>}

        {isString(body) ? (
          <Text variant="bodyM" color={!title ? undefined : "currentColor75"}>
            {body}
          </Text>
        ) : (
          body
        )}

        {buttonValue && (
          <div>
            <Button trailing={<Icon icon="chevronRight" />} variant="primary">
              {buttonValue}
            </Button>
          </div>
        )}
      </Edges>

      {paused && (
        <div className={overlayStyle} style={{ opacity: 0.075 * index }} />
      )}
    </motion.div>
  );
};
